import { LoadingButton } from '@mui/lab'
import { Button } from '@mui/material'
import SignInOptions from 'src/components/SignInOptions'

// ----------------------------------------------------------------------
// The answer to a Tesla, Google or Apple sign-in that no console account
// uses.
//
// Nothing has been made yet. Somebody new wants an account; somebody who
// already has one signed in with another address, came back from Tesla as
// another of their Tesla accounts, or hid theirs from Beast with Apple, and
// wants to be sent to it. The two answers are one choice, so
// they are drawn as one group, at the rhythm of the ways to sign in they
// came from, and "I already have one" is a button as large as the other,
// because that is the answer this step exists to catch.
// ----------------------------------------------------------------------

/** A sign-in that found no account, handed over from its callback page. */
export interface NewAccountQuestion {
  /** What the backend handed back instead of making an account. */
  ticket: string
  via: 'tesla' | 'google' | 'apple'
  /** The address the provider proved, when it gave one. */
  email: string | null
}

const RELAY = '@privaterelay.appleid.com'

const ACCOUNT = {
  tesla: 'Tesla account',
  google: 'Google account',
  apple: 'Apple ID',
} as const

/** What the step says about the account the person signed in with. */
export const newAccountQuestionText = ({
  via,
  email,
}: Pick<NewAccountQuestion, 'via' | 'email'>): string => {
  if (via === 'apple' && email?.toLowerCase().endsWith(RELAY)) {
    return 'No BeastOS account uses this Apple ID. You chose to hide your email from Beast, so it cannot be matched to an account you already have.'
  }
  const account = ACCOUNT[via]
  return email
    ? `No BeastOS account uses the ${account} ${email}.`
    : `No BeastOS account uses this ${account}.`
}

interface NewAccountStepProps {
  loading: boolean
  onCreate: () => void
  onDecline: () => void
}

const NewAccountStep = ({
  loading,
  onCreate,
  onDecline,
}: NewAccountStepProps) => (
  <SignInOptions>
    <LoadingButton
      variant="contained"
      size="large"
      loading={loading}
      onClick={onCreate}
      fullWidth
    >
      Create a BeastOS account
    </LoadingButton>
    <Button
      variant="outlined"
      size="large"
      disabled={loading}
      onClick={onDecline}
      fullWidth
    >
      I already have one
    </Button>
  </SignInOptions>
)

export default NewAccountStep
